import clsx from 'clsx';
import React, { type ReactNode } from 'react';
interface ContentLayoutHeaderProps {
  children: ReactNode;
}

/**
 * @desc ContentLayoutHeader
 * @param children
 * @return {React.JSX.Element}
 * @constructor
 */
function ContentLayoutHeader({ children }: ContentLayoutHeaderProps): React.JSX.Element {
  return (
    <div className="bg-background sticky top-0 z-20 flex min-h-20 w-full min-w-0 overflow-x-hidden pb-2">
      {children}
    </div>
  );
}

ContentLayoutHeader.displayName = 'ContentLayoutHeader';

interface ContentLayoutContentProps {
  children: ReactNode;
  className?: string;
}

/**
 * @desc ContentLayoutContent
 * @param children
 * @param className Optional additional CSS classes (merged with default "h-full")
 * @return {React.JSX.Element}
 * @constructor
 */
function ContentLayoutContent({
  children,
  className,
}: ContentLayoutContentProps): React.JSX.Element {
  return <div className={clsx('h-full w-full min-w-0', className)}>{children}</div>;
}

ContentLayoutContent.displayName = 'ContentLayoutContent';

interface ContentLayoutProps {
  children: ReactNode;
  scrollable?: boolean;
  className?: string;
}

/**
 * @desc ContentLayout
 * @param children
 * @return {React.JSX.Element}
 * @constructor
 */
function ContentLayout({
  children,
  className,
  scrollable = true,
}: ContentLayoutProps): React.JSX.Element {
  let header: ReactNode = null;
  let content: ReactNode = null;

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if ((child.type as any).displayName === 'ContentLayoutHeader') {
        header = child;
      } else if ((child.type as any).displayName === 'ContentLayoutContent') {
        content = child;
      }
    }
  });

  return (
    <div className={clsx('flex h-full w-full min-w-0 flex-1 flex-col overflow-hidden', className)}>
      <div className="h-full min-h-0 w-full min-w-0 flex-1">
        <div className="mx-auto flex h-full w-full min-w-0 flex-col">
          {header}
          <div
            className={clsx(
              'border-gray-medium relative min-h-0 min-w-0 flex-1 rounded-md border bg-white shadow-sm',
              // overflow-y-auto alone promotes overflow-x to auto (Row gutter bleed / wide chrome).
              scrollable ? 'overflow-x-hidden overflow-y-auto' : 'overflow-hidden',
            )}
          >
            <div className="flex h-full min-h-0 min-w-0 flex-col">{content}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

ContentLayout.Header = ContentLayoutHeader;
ContentLayout.Content = ContentLayoutContent;

export default ContentLayout;
